import React, { lazy, Suspense } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import "../assets/styles/global.css";
import { pathPrefix } from "../constants/hiddenConstants";

import Login from "../pages/login";
import Signup from "../pages/signup";
import DashBoard from "../pages/dashboard";
import NotFoundPage from "../pages/notFound";
import ErrorBoundary from "../components/ErrorBoundary";
import ProtectedRoute from "../components/ProtectredRoute/ProtectedRoute";
import { EmptyPosScreen } from "../pages/pos/EmptyPosScreen";
import Loading from "../pages/loading";

const OnBoarding = lazy(() => import("../pages/onboarding"));
const Confirm = lazy(() => import("../pages/confirm"));
const POS = lazy(() => import("../pages/pos/Index"));
const BookingListV2 = lazy(() => import("../pages/bookingListV2"));
const TableView = lazy(() => import("../pages/pos/TableView"));
const NewOrder = lazy(() => import("../pages/pos/NewOrder"));
const CloseOrder = lazy(() => import("../pages/pos/CloseOrder"));
const CMViewPage = lazy(
  () => import("../components/channelManager/CMViewPage"),
);
const ComingSoon = lazy(() => import("../pages/comingSoon"));
const Settings = lazy(() => import("../pages/profile"));
const MainReportsPage = lazy(() => import("../pages/report/MainReportsPage"));
const Home = lazy(() => import("../pages/home"));
const BookingReservationPage = lazy(
  () => import("../pages/bookingReservation"),
);
const KitchenDisplaySystem = lazy(
  () => import("../pages/pos/kitchenDisplaySystem"),
);
const WalkinPageV3 = lazy(() => import("../pages/walkinV3"));
const AllItems = lazy(() => import("../pages/pos/AllItems"));
const PosSetting = lazy(() => import("../pages/pos/PosSetting"));
const CreateNewPos = lazy(() => import("../pages/pos/CreateNewPos"));
const ClientPage = lazy(() => import("../pages/clients"));
const RatesAndAvailabilityPage = lazy(
  () => import("../pages/ratesAndAvailability"),
);
const BookingEngineSettingsPage = lazy(
  () => import("../pages/bookingEngineSettings"),
);
const HousekeepingPage = lazy(() => import("../pages/housekeepingV2"));
const OutOfOrderRoomsPage = lazy(
  () => import("../pages/housekeepingV2/OutOfOrderRoomsPage"),
);
const Party = lazy(() => import("../pages/party"));
const Expense = lazy(() => import("../pages/expense"));
const MobileMenu = lazy(() => import("../pages/mobileMenu/Index"));
const ManageStocksV2 = lazy(() => import("../pages/manageStocksV2"));
const Payments = lazy(() => import("../pages/payments/Index"));
const Invoice = lazy(() => import("../pages/invoice/Index"));
const ReviewsPage = lazy(() => import("../pages/reviews"));

export default function Router() {
  return (
    <BrowserRouter basename={pathPrefix}>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Login />
              </ErrorBoundary>
            }
          />
          <Route
            path="/login"
            element={
              <ErrorBoundary>
                <Login />
              </ErrorBoundary>
            }
          />
          <Route
            path="/signup"
            element={
              <ErrorBoundary>
                <Signup />
              </ErrorBoundary>
            }
          />
          <Route
            path="/onBoarding"
            element={
              <ProtectedRoute>
                <OnBoarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/confirmed"
            element={
              <ProtectedRoute>
                <Confirm />
              </ProtectedRoute>
            }
          />

          <Route path="/dashboard" element={<DashBoard />}>
            <Route
              path=""
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />

            <Route
              path="walkin"
              element={
                <ProtectedRoute>
                  <WalkinPageV3 />
                </ProtectedRoute>
              }
            />

            <Route
              path="bookings"
              element={
                <ProtectedRoute>
                  <BookingListV2 />
                </ProtectedRoute>
              }
            />

            <Route
              path="bookingReservation"
              element={
                <ProtectedRoute>
                  <BookingReservationPage />
                </ProtectedRoute>
              }
            />

            <Route
              path=":slug"
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route path="" element={<EmptyPosScreen />} />
              <Route path="new-order" element={<NewOrder />} />
              <Route path="ingredient" element={<ComingSoon />} />
              <Route path="close-order" element={<CloseOrder />} />
              <Route path="all-items" element={<AllItems />} />
              <Route path="pos-settings" element={<PosSetting />} />

              <Route path="menu-settings" element={<ComingSoon />} />
              <Route path="inventory-purchase" element={<ComingSoon />} />
              <Route path="table-view" element={<TableView />} />
              <Route path="all-orders" element={<POS />} />
              <Route
                path="kitchen-display-system"
                element={<KitchenDisplaySystem />}
              />
            </Route>

            <Route
              path="add-new-pos"
              element={
                <ProtectedRoute>
                  <CreateNewPos />
                </ProtectedRoute>
              }
            />
            <Route
              path="channel-manager-old-version"
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route path="" element={<CMViewPage />} />
            </Route>

            <Route
              path="repair"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="transactions"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />

            <Route
              path="clients"
              element={
                <ProtectedRoute>
                  <ClientPage />
                </ProtectedRoute>
              }
            />

            {/* Only for Mobile Screen */}
            <Route
              path="menu"
              element={
                <ProtectedRoute>
                  <MobileMenu />
                </ProtectedRoute>
              }
            />

            <Route
              path="books"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute>
                  <MainReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="channel-manager"
              element={
                <ProtectedRoute>
                  <RatesAndAvailabilityPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="booking-engine"
              element={
                <ProtectedRoute>
                  <BookingEngineSettingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="house-keeping"
              element={
                <ProtectedRoute>
                  <HousekeepingPage />
                </ProtectedRoute>
              }
            >
              {/* OUT OF ORDER list under /house-keeping/out-of-order */}
              <Route path="out-of-order" element={<OutOfOrderRoomsPage />} />
            </Route>

            <Route
              path="party"
              element={
                <ProtectedRoute>
                  <Party />
                </ProtectedRoute>
              }
            />

            <Route
              path="party/:partyId"
              element={
                <ProtectedRoute>
                  <Party />
                </ProtectedRoute>
              }
            />

            <Route
              path="expense"
              element={
                <ProtectedRoute>
                  <Expense />
                </ProtectedRoute>
              }
            />

            <Route
              path="manageStocks"
              element={
                <ProtectedRoute>
                  <ManageStocksV2 />
                </ProtectedRoute>
              }
            />

            <Route
              path="payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoice"
              element={
                <ProtectedRoute>
                  <Invoice />
                </ProtectedRoute>
              }
            />

            <Route
              path="reviews"
              element={
                <ProtectedRoute>
                  <ReviewsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            >
              <Route path="account" />
              <Route path="bank-details" />
              <Route path="rooms" />
              <Route path="pos" />
              <Route path="channels" />
              <Route path="users-roles" />
              <Route path="tax" />
              <Route path="addons" />
              <Route path="policies" />
              <Route path="engine" />
              <Route path="promo-code-settings" />
              <Route path="reservations-invoice" />
              <Route path="night-audit" />
              <Route path="billing" />
            </Route>
          </Route>

          <Route path="/*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
